import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock,
  FileText,
  User,
  LogOut,
  Settings,
  BarChart3,
  Calendar
} from 'lucide-react';
import { useAuthStore } from '../auth/authStore';
import PunchButton from '../attendance/PunchButton';
import { AttendanceProvider } from '../../contexts/AttendanceContext';
import toast from 'react-hot-toast';
import { NotificationBell } from '@/components/notifications/NotificationBell';

interface ClientDashboardLayoutProps {
  children: React.ReactNode;
  currentPage?: string;
}

export const ClientDashboardLayout: React.FC<ClientDashboardLayoutProps> = ({ 
  children, 
  currentPage = 'dashboard' 
}) => {
  const { user, tenant, logout } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/client/dashboard',
      icon: BarChart3,
      current: currentPage === 'dashboard'
    },
    {
      name: 'Time Tracking',
      href: '/client/time-tracking',
      icon: Clock,
      current: currentPage === 'time-tracking'
    },
    {
      name: 'My Tasks',
      href: '/client/tasks',
      icon: FileText,
      current: currentPage === 'tasks'
    },
    {
      name: 'Reports',
      href: '/client/reports',
      icon: BarChart3,
      current: currentPage === 'reports'
    }
  ];

  return (
    <AttendanceProvider>
      <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-bold text-primary-600">ERPCA</h1>
              {tenant && (
                <div className="ml-4 text-sm text-gray-500">
                  {tenant.name}
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center text-sm text-gray-700">
                <User className="w-4 h-4 mr-2" />
                {user?.firstName} {user?.lastName}
              </div>
              
              <div className="flex items-center space-x-2">
                <PunchButton compact />

                <NotificationBell />

                <button
                  onClick={() => navigate('/client/settings')}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                  title="Settings"
                >
                  <Settings className="w-5 h-5" />
                </button>

                <button
                  onClick={handleLogout}
                  className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100"
                  title="Logout"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex">
          {/* Sidebar Navigation */}
          <nav className="w-64 mr-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="space-y-2">
                {navigationItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.name}
                      onClick={() => navigate(item.href)}
                      className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        item.current
                          ? 'bg-primary-100 text-primary-700 border border-primary-200'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-3" />
                      {item.name}
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Client Info Card */}
            <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Client Information</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Name:</span>
                  <span className="text-gray-900">{user?.firstName} {user?.lastName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Email:</span>
                  <span className="text-gray-900">{user?.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Role:</span>
                  <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                    Client
                  </span>
                </div>
              </div>
            </div>
          </nav>

          {/* Main Content */}
          <main className="flex-1">
            {children}
          </main>
        </div>
      </div>
    </div>
    </AttendanceProvider>
  );
};
